// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { GetOrdersApiParameter } from "@insite/client-framework/Services/OrderService";
import { GetQuotesApiParameter } from "@insite/client-framework/Services/QuoteService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadQuotes from "@insite/client-framework/Store/Data/Quotes/Handlers/LoadQuotes";
import { getQuotesDataView } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import reorder from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/Reorder";
import updateSearchFields from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ClickableProps } from "@insite/mobius/Clickable/Clickable";
import DataTable, { DataTableProps, SortOrderOptions } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner/LoadingSpinner";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    numberOfRecords = "numberOfRecords",
    title = "title",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
        [fields.title]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        settingsCollection: getSettingsCollection(state),
        session,
        quotesDataView: getQuotesDataView(state, recentQuotesParameter),
        myQuotesPageNavLink: getPageLinkByPageType(state, "RfqMyQuotesPage"),
        canViewQuotes: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
        language: state.context.session.language,
        parameter: state.pages.orderHistory.getOrdersParameter,
        QuoteDetailsPageUrl: getPageLinkByPageType(state, "RfqQuoteDetailsPage")?.url,
    };
};

export interface RecentQuotesStyles {
    container?: InjectableCss;
    headerClickable?: ClickableProps;
    headerText?: TypographyProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
    orderDateHeader?: DataTableHeaderProps;
    orderNumberHeader?: DataTableHeaderProps;
    shipToHeader?: DataTableHeaderProps;
    statusHeader?: DataTableHeaderProps;
    customerPOHeader?: DataTableHeaderProps;
    orderTotalHeader?: DataTableHeaderProps;
    reorderHeader?: DataTableHeaderProps;
    orderDateCells?: DataTableCellProps;
    orderNumberCells?: DataTableCellProps;
    shipToCells?: DataTableCellProps;
    statusCells?: DataTableCellProps;
    customerPOCells?: DataTableCellProps;
    orderTotalCells?: DataTableCellProps;
    reorderCells?: DataTableCellProps;
    dataTable?: DataTableProps;
    quoteLink?: LinkPresentationProps;
}

const mapDispatchToProps = {
    loadQuotes,
    updateSearchFields,
    reorder,
};

type Props = ReturnType<typeof mapStateToProps> &
    OwnProps &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    HasToasterContext;

const recentQuotesParameter: GetQuotesApiParameter = {
    page: 1,
    sort: "OrderDate DESC",
};

const recentQuotesStyles: RecentQuotesStyles = {
    container: {
        css: css`
            overflow: auto;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        color: "primary.main",
        css: css`
            margin: auto;
        `,
    },
    headerClickable: {
        css: css`
            width: 100%;
            justify-content: space-between;
        `,
    },
    shipToHeader: {
        tight: true,
    },
    orderTotalHeader: {
        tight: true,
        alignX: "right",
    },
    reorderHeader: {
        tight: true,
    },
    orderTotalCells: {
        alignX: "right",
    },
    shipToCells: {
        typographyProps: {
            ellipsis: true,
            css: css`
                display: block;
                max-width: 300px;
            `,
        },
    },
};

const styles = recentQuotesStyles;

class RecentQuotes extends React.Component<Props> {
    componentDidMount() {
        recentQuotesParameter.pageSize = this.props.fields.numberOfRecords;

        if (this.props.canViewQuotes && !this.props.quotesDataView.value) {
            this.props.loadQuotes(recentQuotesParameter);
        }
    }

    headerClick(sortField: string) {
        const sort = this.props.parameter.sort === sortField ? `${sortField} DESC` : sortField;

        recentQuotesParameter.sort = sort;

        this.props.updateSearchFields({ sort });
        this.props.loadQuotes(recentQuotesParameter);
    }

    sorted = (sortField: string) => {
        let sorted: boolean | string = false;

        if (this.props.parameter.sort === sortField) {
            sorted = "ascending";
        } else if (this.props.parameter.sort === `${sortField} DESC`) {
            sorted = "descending";
        }

        return sorted as SortOrderOptions;
    };

    handleRedirectToDetails(id: string) {
        const { history, QuoteDetailsPageUrl } = this.props;

        if (id) {
            history.push(`${QuoteDetailsPageUrl}?quoteId=${id}`);
        }
    }

    render() {
        const { quotesDataView, myQuotesPageNavLink, fields } = this.props;
        const myQuotesPageUrl = myQuotesPageNavLink ? myQuotesPageNavLink.url : undefined;
        const quotes = quotesDataView.value;

        if (!quotes) {
            return (
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner} />
                </StyledWrapper>
            );
        }

        if (quotes.length === 0) {
            return null;
        }

        return (
            <StyledWrapper>
                <StyledWrapper
                    css={css`
                        width: 100%;
                        display: flex;
                        justify-content: space-between;
                    `}
                >
                    <Typography variant="h2">{fields.title || translate("Recent Quotes")}</Typography>
                    <Link href={myQuotesPageUrl}>{translate("View All")}</Link>
                </StyledWrapper>
                <StyledWrapper {...styles.container} data-test-selector="orderHistoryTable">
                    <DataTable {...styles.dataTable}>
                        <DataTableHead>
                            <DataTableHeader {...styles.orderNumberHeader} title={translate("Quote Number")}>
                                {translate("Quote #")}
                            </DataTableHeader>
                            <DataTableHeader {...styles.statusHeader}>{translate("Online Ref #")}</DataTableHeader>
                            <DataTableHeader
                                {...styles.statusHeader}
                                sorted={this.sorted("status")}
                                onSortClick={() => this.headerClick("status")}
                            >
                                {translate("Status", "status")}
                            </DataTableHeader>
                            <DataTableHeader
                                {...styles.orderDateHeader}
                                sorted={this.sorted("orderDate")}
                                onSortClick={() => this.headerClick("orderDate")}
                            >
                                {translate("ٌRequested Date")}
                            </DataTableHeader>
                            <DataTableHeader
                                {...styles.orderTotalHeader}
                                sorted={this.sorted("expirationDate")}
                                onSortClick={() => this.headerClick("expirationDate")}
                            >
                                {translate("Expires")}
                            </DataTableHeader>
                            <DataTableHeader
                                {...styles.shipToHeader}
                                sorted={this.sorted("shipToFullAddress")}
                                onSortClick={() => this.headerClick("shipToFullAddress")}
                            >
                                {translate("Ship To Address")}
                            </DataTableHeader>
                        </DataTableHead>
                        <DataTableBody data-test-selector="orderHistoryTable_tableBody">
                            {quotes.map(
                                ({
                                    id,
                                    erpOrderNumber,
                                    shipToFullAddress,
                                    statusDisplay,
                                    orderNumber,
                                    expirationDate,
                                    orderDate,
                                }) => {
                                    return (
                                        <DataTableRow key={id} onClick={() => this.handleRedirectToDetails(id)}>
                                            <DataTableCell
                                                {...styles.orderNumberCells}
                                                data-test-selector="orderHistoryTable_tableCell_orderNumber"
                                            >
                                                {erpOrderNumber || translate("Pending")}
                                            </DataTableCell>
                                            <DataTableCell
                                                {...styles.statusCells}
                                                data-test-selector="orderHistoryTable_tableCell_onlineRef"
                                            >
                                                {orderNumber}
                                            </DataTableCell>
                                            <DataTableCell
                                                {...styles.statusCells}
                                                data-test-selector="orderHistoryTable_tableCell_status"
                                            >
                                                {statusDisplay}
                                            </DataTableCell>
                                            <DataTableCell
                                                {...styles.orderDateCells}
                                                data-test-selector="orderHistoryTable_tableCell_date"
                                            >
                                                {orderDate
                                                    ? getLocalizedDateTime({
                                                          dateTime: new Date(orderDate),
                                                          language: this.props.language,
                                                      })
                                                    : "--"}
                                            </DataTableCell>
                                            <DataTableCell {...styles.customerPOCells}>
                                                {expirationDate
                                                    ? getLocalizedDateTime({
                                                          dateTime: new Date(expirationDate),
                                                          language: this.props.language,
                                                      })
                                                    : "--"}
                                            </DataTableCell>
                                            <DataTableCell {...styles.shipToCells}>{shipToFullAddress}</DataTableCell>
                                        </DataTableRow>
                                    );
                                },
                            )}
                        </DataTableBody>
                    </DataTable>
                </StyledWrapper>
            </StyledWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(withToaster(RecentQuotes))),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 0,
            },
            {
                name: fields.numberOfRecords,
                displayName: "Number of Recent Quotes Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 5,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
    },
};

export default widgetModule;
